import { Button } from "@mui/material";
import { TextEditor, TextInput, useMuiController } from "@sinch/components";
import { useEmailPreview } from "@sinch/components/application/Messages/useEmailPreview";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { ensureArray } from "ramda-adjunct";
import React, { FC } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MessageFormFields } from "./formSchema";
import { AvailableTemplateBox } from "./AvailableTemplateBox";
import type { Placeholders } from "./MessageForm";

interface EmailContentProps {
  /** Whether to disable the subject input field. */
  disableSubject?: boolean;
  placeholders?: Placeholders;
  context: Record<string, any>;
  fillDefaultTemplate?: boolean;
}

/**
 * EmailContent is a component that renders inputs for composing an email message.
 * It displays a subject input (if not disabled) and a text editor for the email message.
 */
export const EmailContent: FC<EmailContentProps> = ({ disableSubject, placeholders, context, fillDefaultTemplate }) => {
  const { t } = useTranslation();
  const { openPreviewInNewTab } = useEmailPreview(
    disableSubject ? "/admin/emailNewsletters/previewReconfirmation" : "/admin/emailNewsletters/preview"
  );

  const { field: subjectField } = useMuiController<MessageFormFields, "subject">({
    name: "subject",
    disabled: disableSubject,
  });
  const { field: messageField } = useMuiController<MessageFormFields, "message">({ name: "message" });

  const [recipients, userGroups] = useWatch<MessageFormFields>({
    name: ["recipients", "userGroups"],
  });

  const handlePreview = () => {
    openPreviewInNewTab({
      recipients: [...ensureArray(recipients), ...ensureArray(userGroups)],
      subject: subjectField.value,
      message: messageField.value,
      additionalFields: context,
    });
  };

  return (
    <>
      <AvailableTemplateBox
        channel={NotificationChannel.Email}
        fillDefaultTemplate={fillDefaultTemplate}
        onApplyTemplate={({ subject, content }) => {
          subjectField.onChange(subject);
          messageField.onChange(content);
        }}
      />
      {!disableSubject && <TextInput fullWidth label={t("Messages.emailSubjectInputLabel")} {...subjectField} />}
      <TextEditor
        enableImageInsert
        fullWidth
        label={t("Messages.messageInputLabel")}
        minRows={8}
        multiline
        placeholders={placeholders}
        secondaryToolbar={
          <Button color="info" onClick={handlePreview} size="small" variant="text">
            {t("Messages.emailPreview")}
          </Button>
        }
        {...messageField}
      />
    </>
  );
};
